import { accessToken, login } from '@/auth/oidc'
import { ApiError, BASE_URL, request } from '@/lib/api'
import type { FilterSpec, ImportReport } from './types'

/**
 * Импорт с сопоставлением колонок и массовые доменные действия (тикет #107).
 */

export interface ImportField {
  name: string
  /** Подпись для экрана; у полей каталога её нет — их подписывает словарь (`column.*`). */
  label: string | null
  required: boolean
  hint: string | null
}

export interface ImportTarget {
  key: string
  resource: string
  fields: ImportField[]
}

export interface ImportPreview {
  header: string[]
  sample: string[][]
  rows: number
  fields: ImportField[]
  /** Поле → заголовок файла, где заголовок явно совпал с полем. */
  suggested: Record<string, string>
}

export const fetchImportTargets = (signal?: AbortSignal) =>
  request<{ targets: ImportTarget[] }>('/import/targets', { signal }).then((r) => r.targets)

/**
 * Multipart вручную, мимо `request`: границу multipart браузер вычисляет сам, и заданный
 * руками `Content-Type` сделал бы тело неразбираемым.
 */
async function upload<T>(path: string, file: File, mapping?: Record<string, string>): Promise<T> {
  const token = await accessToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const body = new FormData()
  body.append('file', file)
  if (mapping) body.append('mapping', JSON.stringify(mapping))
  const response = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body })
  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(response.status, problem?.code ?? null, problem?.detail ?? problem?.message ?? response.statusText)
  }
  return (await response.json()) as T
}

export const previewImport = (target: string, file: File) =>
  upload<ImportPreview>(`/import/${encodeURIComponent(target)}/preview`, file)

export const runImport = (target: string, file: File, mapping: Record<string, string>) =>
  upload<ImportReport>(`/import/${encodeURIComponent(target)}`, file, mapping)

export interface BulkSelection {
  ids?: string[]
  filters?: FilterSpec[]
}

export interface BulkDomainResult {
  requested: number
  affected: number
  skipped: { id: string; reason: string }[]
}

export const bulkReassign = (entity: string, selection: BulkSelection, employeeId: string) =>
  request<BulkDomainResult>(`/lists/${encodeURIComponent(entity)}/bulk/reassign`, {
    method: 'POST',
    body: { ...selection, employeeId },
  })

export const bulkStage = (selection: BulkSelection, stageCode: string) =>
  request<BulkDomainResult>('/lists/sales.deal/bulk/stage', { method: 'POST', body: { ...selection, stageCode } })
